import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";

import type { Task } from "features/Resources/Task/models";
import { taskKeys } from "features/Resources/Task/hooks";
import * as api from "../api";
import type { CustomForm, CustomFormResponse, CustomFormResponseSummary } from "../types";
import type {
  AssignFormPayload,
  CreateCustomFormRequest,
  SubmitCustomFormResponseRequest,
  UpdateCustomFormRequest,
} from "../models/payloads";

export const customFormKeys = {
  all: ["customForms"] as const,
  lists: () => [...customFormKeys.all, "list"] as const,
  list: (queryString: string) => [...customFormKeys.lists(), queryString] as const,
  details: () => [...customFormKeys.all, "detail"] as const,
  // String(id): callers pass the numeric blueprint id or the string useParams id —
  // without normalizing they'd be two cache entries for the same form.
  detail: (id: number | string) => [...customFormKeys.details(), String(id)] as const,
  responses: (formId: number | string) => [...customFormKeys.detail(formId), "responses"] as const,
  responseForTask: (taskId: number | string) => ["taskCustomFormResponse", String(taskId)] as const,
  assignments: (formId: number | string) => [...customFormKeys.detail(formId), "assignments"] as const,
  myAssignments: (formUuid: string) => ["customFormMyAssignments", formUuid] as const,
};

export function useCustomFormQuery(id: number | string | null | undefined) {
  return useQuery<CustomForm>({
    queryKey: customFormKeys.detail(id ?? ""),
    queryFn: () => api.getCustomForm(id as number),
    enabled: !!id,
    staleTime: 1000 * 60 * 3,
    // 404 (archived/deleted form) is deterministic; don't retry.
    retry: false,
  });
}

export function useCreateCustomForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCustomFormRequest) => api.createCustomForm(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.all });
    },
  });
}

export function useUpdateCustomForm(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCustomFormRequest) => api.updateCustomForm(id, payload),
    onSuccess: (form) => {
      queryClient.setQueryData(customFormKeys.detail(id), form);
      queryClient.invalidateQueries({ queryKey: customFormKeys.lists() });
    },
  });
}

export function useDeleteCustomForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => api.deleteCustomForm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.all });
    },
  });
}

export function useCustomFormDeleteImpactQuery(id: number | string | null | undefined, enabled: boolean) {
  return useQuery<api.CustomFormDeleteImpact>({
    queryKey: [...customFormKeys.detail(id ?? ""), "delete-impact"],
    queryFn: () => api.fetchCustomFormDeleteImpact(id as number | string),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

export function useCustomFormResponseForTaskQuery(taskId: number | string | null | undefined) {
  return useQuery<CustomFormResponseSummary | null>({
    queryKey: customFormKeys.responseForTask(taskId ?? ""),
    queryFn: () => api.getCustomFormResponseForTask(taskId as number),
    enabled: !!taskId,
    staleTime: 1000 * 60 * 3,
    retry: false,
  });
}

export function useSubmitCustomFormResponse(taskId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubmitCustomFormResponseRequest) => api.submitCustomFormResponse(taskId, payload),
    onSuccess: (response) => {
      queryClient.setQueryData(customFormKeys.responseForTask(taskId), response);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["newHireJourneys"] });
    },
  });
}

export function useCustomFormResponseQuery(id: number | string | null | undefined) {
  return useQuery<CustomFormResponse>({
    queryKey: ["customFormResponses", "detail", id ?? ""],
    queryFn: () => api.getCustomFormResponse(id as number),
    enabled: !!id,
    staleTime: 1000 * 60 * 3,
  });
}

export function useDeleteCustomFormResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => api.deleteCustomFormResponse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.all });
    },
  });
}

export function useFormAssignmentsQuery(formId: number | string | null | undefined) {
  return useQuery<Task[]>({
    queryKey: customFormKeys.assignments(formId ?? ""),
    queryFn: async () => (await api.fetchFormAssignments(formId as number | string)).data,
    enabled: !!formId,
    staleTime: 1000 * 60 * 3,
  });
}

export function useMyFormAssignmentsQuery(formUuid: string | null | undefined) {
  return useQuery<Task[]>({
    queryKey: customFormKeys.myAssignments(formUuid ?? ""),
    queryFn: async () => (await api.fetchMyAssignments(formUuid as string)).data,
    enabled: !!formUuid,
    staleTime: 1000 * 60 * 3,
  });
}

export function useAssignForm(formId: number | string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (payload: AssignFormPayload) => api.assignForm(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customFormKeys.assignments(formId) });
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      toast({ title: "Assigned", status: "success" });
    },
  });
}
